import Dialog from "@mui/material/Dialog";
import { alpha } from "@mui/material/styles";
import styled from "../../util/styled";

const PREFIX = "CookieConsent";

export const classes = {
  bannerInner: `${PREFIX}-bannerInner`,
  bannerCopy: `${PREFIX}-bannerCopy`,
  bannerIcon: `${PREFIX}-bannerIcon`,
  bannerActions: `${PREFIX}-bannerActions`,
  actionButton: `${PREFIX}-actionButton`,
  policyLink: `${PREFIX}-policyLink`,
  dialogIntro: `${PREFIX}-dialogIntro`,
  categoryList: `${PREFIX}-categoryList`,
  category: `${PREFIX}-category`,
  categoryHeader: `${PREFIX}-categoryHeader`,
  categoryIcon: `${PREFIX}-categoryIcon`,
  categoryCopy: `${PREFIX}-categoryCopy`,
  categoryMeta: `${PREFIX}-categoryMeta`,
  dialogActions: `${PREFIX}-dialogActions`,
};

export const Banner = styled(Dialog)(({ theme }) => ({
  [`& .MuiDialog-container`]: {
    alignItems: "flex-end",
  },

  [`& .MuiDialog-paper`]: {
    width: "100%",
    maxWidth: "none",
    margin: 0,
    padding: theme.spacing(2.5, 3),
    color: theme.palette.innoGrey.dark,
    backgroundColor: theme.palette.common.white,
    borderTop: `4px solid ${theme.palette.innoBlue.main}`,
    borderRadius: 0,
    boxShadow: `0 -12px 36px ${alpha(theme.palette.common.black, 0.18)}`,
  },

  [`& .${classes.bannerInner}`]: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2.5),
    width: "100%",
    maxWidth: 1280,
    margin: "0 auto",
  },

  [`& .${classes.bannerCopy}`]: {
    display: "flex",
    flex: "1 1 480px",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
  },

  [`& .${classes.bannerIcon}`]: {
    flex: "0 0 auto",
    marginTop: theme.spacing(0.25),
    color: theme.palette.innoBlue.main,
    fontSize: 30,
  },

  [`& .${classes.bannerActions}`]: {
    display: "flex",
    flex: "0 0 auto",
    alignItems: "center",
    gap: theme.spacing(1),
  },

  [`& .${classes.actionButton}`]: {
    minHeight: 44,
    padding: theme.spacing(1, 2.25),
    whiteSpace: "nowrap",
    fontWeight: 700,
    textTransform: "none",
    "&.MuiButton-contained": {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.innoBlue.dark,
      "&:hover": {
        backgroundColor: theme.palette.innoBlue.dark,
        boxShadow: theme.shadows[4],
      },
    },
    "&.MuiButton-outlined": {
      color: theme.palette.innoBlue.dark,
      borderColor: theme.palette.innoBlue.dark,
    },
  },

  [`& .${classes.policyLink}`]: {
    color: theme.palette.innoBlue.dark,
    fontWeight: 700,
  },

  [theme.breakpoints.down("md")]: {
    [`& .MuiDialog-paper`]: {
      padding: theme.spacing(2, 2),
    },

    [`& .${classes.bannerInner}`]: {
      alignItems: "stretch",
      flexDirection: "column",
      gap: theme.spacing(1.5),
    },

    [`& .${classes.bannerCopy}`]: {
      flexBasis: "auto",
    },

    [`& .${classes.bannerActions}`]: {
      flexWrap: "wrap",
    },

    [`& .${classes.actionButton}`]: {
      flex: "1 1 160px",
    },
  },

  [theme.breakpoints.down("sm")]: {
    [`& .MuiDialog-paper`]: {
      maxHeight: "90vh",
      overflowY: "auto",
    },

    [`& .${classes.bannerActions}`]: {
      flexDirection: "column",
    },

    [`& .${classes.actionButton}`]: {
      width: "100%",
      flexBasis: "auto",
    },
  },
}));

export const PreferencesDialog = styled(Dialog)(({ theme }) => ({
  [`& .MuiDialog-paper`]: {
    width: "100%",
    maxWidth: 680,
    borderRadius: 16,
  },

  [`& .${classes.dialogIntro}`]: {
    color: theme.palette.text.secondary,
  },

  [`& .${classes.categoryList}`]: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },

  [`& .${classes.category}`]: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.innoGrey.light}`,
    borderRadius: 12,
    backgroundColor: alpha(theme.palette.innoBlue.light, 0.1),
  },

  [`& .${classes.categoryHeader}`]: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
  },

  [`& .${classes.categoryIcon}`]: {
    flex: "0 0 auto",
    marginTop: theme.spacing(0.25),
    color: theme.palette.innoBlue.main,
    fontSize: 24,
  },

  [`& .${classes.categoryCopy}`]: {
    flex: "1 1 auto",
  },

  [`& .${classes.categoryMeta}`]: {
    display: "block",
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },

  [`& .${classes.dialogActions}`]: {
    display: "flex",
    gap: theme.spacing(1),
    padding: theme.spacing(2, 3, 3),
    "& > :not(:first-of-type)": {
      marginLeft: 0,
    },
  },

  [`& .${classes.actionButton}`]: {
    minHeight: 44,
    fontWeight: 700,
    textTransform: "none",
    "&.MuiButton-contained": {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.innoBlue.dark,
      "&:hover": {
        backgroundColor: theme.palette.innoBlue.dark,
        boxShadow: theme.shadows[4],
      },
    },
    "&.MuiButton-outlined": {
      color: theme.palette.innoBlue.dark,
      borderColor: theme.palette.innoBlue.dark,
    },
  },

  [theme.breakpoints.down("sm")]: {
    [`& .MuiDialog-paper`]: {
      maxHeight: "calc(100% - 24px)",
      margin: 12,
      borderRadius: 12,
    },

    [`& .${classes.dialogActions}`]: {
      alignItems: "stretch",
      flexDirection: "column",
      padding: theme.spacing(1.5, 2, 2),
    },

    [`& .${classes.actionButton}`]: {
      width: "100%",
    },
  },
}));
